import classNames from 'classnames';
import { isNumber } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import {
    getFramework,
    getOperateDirection,
    getOperateType,
    MoveListenerLevel,
    useIdentity,
    useMoveEventListener,
    useScale,
} from '@/framework';
import { useRotateCursor } from '@/utils/cursor-kit';
import { ITouchKitEvent } from '@/utils/event-kit';
import { removeClassesByPrefix } from '@/utils/unit-kit';
import {
    BaseLayerViewType,
    HIDDEN_OPERATE_MIN_HEIGHT,
    HIDDEN_OPERATE_MIN_WIDTH,
    ILayerProps,
    RotateOperateType,
} from '@/version-types';
import { useRotateOperate } from '../hooks/use-rotate-operate';

export const RotateOperate: React.FC<{ layer: ILayerProps }> = ({ layer }) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const rotateOperate = useRotateOperate();
    const [hidden, setHidden] = useState(false);
    const { width, height } = layer;

    const [rotate, setRotate] = useState(layer.rotate);
    const { sideCursor } = useRotateCursor(rotate);
    const scale = useScale();
    const scaleWidth = width * scale;
    const scaleHeight = height * scale;
    const minHide = scaleWidth <= HIDDEN_OPERATE_MIN_WIDTH || scaleHeight <= HIDDEN_OPERATE_MIN_HEIGHT;
    const preCls = 'operate-rotate';

    function checkCanRotate(e: ITouchKitEvent) {
        if (framework.operate.isDisableRotate()) return;
        const target = e.target;
        if (!target) return;
        const operateType = getOperateType(target);
        const direction = getOperateDirection(target);
        const cursorKey = direction?.toUpperCase();

        if (operateType !== BaseLayerViewType.ROTATE) {
            return;
        }
        return { id: layer.id, direction, operateType, cursor: sideCursor[cursorKey] || preCls };
    }

    useEffect(() => {
        setRotate(layer.rotate);
    }, [layer.id]);

    const rotateInfo = useRef<{
        id: string;
        direction: RotateOperateType;
        operateType: BaseLayerViewType;
        cursor: string;
    } | null>(null);

    useMoveEventListener(
        (e) => {
            if (e.isEnd) {
                setHidden(false);
                if (rotateInfo.current) {
                    removeClassesByPrefix(document.body, preCls);
                }
            }
            if (e.isStart) {
                setHidden(true);
            }
            if (!rotateInfo.current) {
                const res = checkCanRotate(e);
                if (!res) return;
                rotateInfo.current = res;
            }
            if (e.isStart && rotateInfo.current) {
                rotateOperate.beforeRotate(rotateInfo.current.id);
                if (rotateInfo.current.operateType === BaseLayerViewType.ROTATE) {
                    document.body.classList.add(rotateInfo.current.cursor);
                }
            }
        },
        MoveListenerLevel.PREPARE_LEVEL,
        'rotate-pre',
    );

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            if (!rotateInfo.current) return null;
            const rotate = rotateOperate.rotate(rotateInfo.current.id, e);
            if (isNumber(rotate)) {
                setRotate(rotate);
            }
            if (e.isEnd) {
                rotateInfo.current = null;
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'rotate-default',
    );

    const [isDisableRotate, setIsDisableRotate] = useState(framework.operate.isDisableRotate());

    useEffect(() => {
        const handler = framework.events.onEditorOperateState((state) => {
            setIsDisableRotate(state.disableRotate);
        });

        return () => {
            framework.events.off(handler);
        };
    }, []);

    if (isDisableRotate) return null;

    return (
        <div
            className={classNames('active-operate__rotate', {
                'is-hidden': hidden,
                mini: minHide,
            })}
        >
            <div className={classNames('rotate operate-rotate')} data-operate-type={BaseLayerViewType.ROTATE}>
                <div className="rotate-icon">
                    <IconFont fontSize={14} name="RefreshBold" />
                </div>
            </div>
            <div className={classNames('rotate operate-move')} data-operate-type={BaseLayerViewType.MOVE}>
                <div className="rotate-icon">
                    <IconFont fontSize={16} name="ArrowFourDirectional" />
                </div>
            </div>
            {/* 左上 */}
            <div
                className={classNames('side-rotate', RotateOperateType.LT, sideCursor.LT)}
                data-operate-type={BaseLayerViewType.ROTATE}
                data-direction={RotateOperateType.LT}
            ></div>
            {/* 右上 */}
            <div
                className={classNames('side-rotate', RotateOperateType.RT, sideCursor.RT)}
                data-operate-type={BaseLayerViewType.ROTATE}
                data-direction={RotateOperateType.RT}
            ></div>
            {/* 左下 */}
            <div
                className={classNames('side-rotate', RotateOperateType.LB, sideCursor.LB)}
                data-operate-type={BaseLayerViewType.ROTATE}
                data-direction={RotateOperateType.LB}
            ></div>
            {/* 右下 */}
            <div
                className={classNames('side-rotate', RotateOperateType.RB, sideCursor.RB)}
                data-operate-type={BaseLayerViewType.ROTATE}
                data-direction={RotateOperateType.RB}
            ></div>
        </div>
    );
};
